import { Box, Button, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { PERMISSION_VALUES } from "components/users/permissions.ts";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { Permission } from "store/api/generatedApi";

const PermissionPickerButton = ({
  label,
  busy,
  onSelect,
}: {
  label: string;
  busy: boolean;
  onSelect: (permission: Permission) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Button
        size="small"
        disabled={busy}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => {
          setAnchorEl(null);
        }}
        slotProps={{ paper: { sx: { maxHeight: 320 } } }}
      >
        {PERMISSION_VALUES.map((permission) => (
          <MenuItem
            key={permission}
            onClick={() => {
              onSelect(permission);
              setAnchorEl(null);
            }}
          >
            {permission}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const UserBulkActionBar = ({
  selectedCount,
  busy,
  onClear,
  onEnable,
  onDisable,
  onGrantPermission,
  onRevokePermission,
  onResend,
  onDelete,
}: {
  selectedCount: number;
  busy: boolean;
  onClear: () => void;
  onEnable?: () => void;
  onDisable?: () => void;
  onGrantPermission?: (permission: Permission) => void;
  onRevokePermission?: (permission: Permission) => void;
  onResend?: () => void;
  onDelete?: () => void;
}) => {
  const { t } = useTenantTranslation();

  if (selectedCount === 0) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        p: 1,
        px: 2,
        bgcolor: "action.hover",
        borderRadius: 1,
        flexWrap: "wrap",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t("nSelected", { count: selectedCount })}
      </Typography>
      {onEnable ? (
        <Button size="small" disabled={busy} onClick={onEnable}>
          {t("enable")}
        </Button>
      ) : null}
      {onDisable ? (
        <Button size="small" disabled={busy} onClick={onDisable}>
          {t("disable")}
        </Button>
      ) : null}
      {onGrantPermission ? (
        <PermissionPickerButton
          label={t("grantPermission")}
          busy={busy}
          onSelect={onGrantPermission}
        />
      ) : null}
      {onRevokePermission ? (
        <PermissionPickerButton
          label={t("revokePermission")}
          busy={busy}
          onSelect={onRevokePermission}
        />
      ) : null}
      {onResend ? (
        <Button size="small" disabled={busy} onClick={onResend}>
          {t("resendInvites")}
        </Button>
      ) : null}
      {onDelete ? (
        <Button size="small" color="error" disabled={busy} onClick={onDelete}>
          {t("deleteInvites")}
        </Button>
      ) : null}
      <Box sx={{ marginLeft: "auto" }}>
        <Button size="small" onClick={onClear}>
          {t("clearSelection")}
        </Button>
      </Box>
    </Stack>
  );
};
